import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { recommendationsApi, type Recommendation } from '../api/modules';
import { RecommendationDrawer } from '../drawers/RecommendationDrawer';
import { relativeTime } from '../lib/format';

function RecCard({ rec, idx, onClick }: { rec: Recommendation; idx?: number; onClick: () => void }) {
  return (
    <div onClick={onClick} className="px-5 py-4 hover:bg-cream-50 cursor-pointer">
      <div className="flex items-start gap-3">
        {idx !== undefined && (
          <span className="w-7 h-7 rounded-full bg-saffron-100 text-saffron-700 flex items-center justify-center flex-shrink-0 text-xs font-semibold font-numeric">
            {String(idx + 1).padStart(2, '0')}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[11px] mb-1">
            <span className="pill pill-navy">{rec.category}</span>
            <span className="text-navy-800/50 font-numeric">{relativeTime(rec.created_at)}</span>
            <span className="text-navy-800/30">·</span>
            <span className="text-navy-800/50">For: <span className="font-medium text-navy-800/70">{rec.target}</span></span>
          </div>
          <div className="text-sm text-navy-800 leading-snug">{rec.text}</div>
          <div className="mt-2 flex items-center gap-4 text-[11px]">
            <span className="text-navy-800/50 font-numeric">
              Confidence <span className={`font-medium ${rec.confidence >= 90 ? 'text-green-700' : rec.confidence >= 80 ? 'text-saffron-600' : 'text-navy-800/70'}`}>
                {rec.confidence}%
              </span>
            </span>
            <span className="text-navy-800/40">·</span>
            <span className="text-navy-800/50">Source: {rec.source}</span>
            <span className="ml-auto text-saffron-600 font-medium">{rec.primary_action} →</span>
          </div>
          {rec.outcome && (
            <div className={`mt-2 text-[11px] flex items-center gap-1.5 ${rec.status === 'accepted' ? 'text-green-700' : 'text-red-600'}`}>
              {rec.status === 'accepted'
                ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg>
              }
              {rec.outcome}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function AIRecommendationsPage() {
  const [selected, setSelected] = useState<string | null>(null);

  const all = useQuery({
    queryKey: ['recommendations', 'all'],
    queryFn: () => recommendationsApi.list({ status: 'any', page_size: 100 }),
  });

  const open = all.data?.data.filter((r) => r.status === 'open') ?? [];
  const accepted = all.data?.data.filter((r) => r.status === 'accepted') ?? [];
  const rejected = all.data?.data.filter((r) => r.status === 'rejected') ?? [];
  const decided = accepted.length + rejected.length;
  const acceptanceRate = decided === 0 ? 0 : Math.round((accepted.length / decided) * 100);

  return (
    <>
      <Header breadcrumbs={['AI Engine', 'Recommendations']} />
      <div className="px-8 pt-7 pb-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-navy-800/50 mb-1">CCC-AI Engine</div>
          <h1 className="serif-display text-3xl text-navy-800 font-medium">AI Recommendations</h1>
          <p className="text-sm text-navy-800/60 mt-1">
            Actionable suggestions from the AI engine · Acceptance rate{' '}
            <span className="text-green-700 font-medium">{acceptanceRate}%</span> over last 90 days
          </p>
        </div>
      </div>

      <div className="px-8 py-5 space-y-5 overflow-y-auto">
        {/* Status summary */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-white border border-saffron-200 p-4 rounded-sm relative">
            <div className="absolute top-0 left-0 w-1 h-full bg-saffron-500" />
            <div className="text-[10px] uppercase tracking-widest text-saffron-700 ml-2">Open</div>
            <div className="font-numeric serif-display text-2xl text-navy-800 font-medium mt-1 ml-2">{open.length}</div>
            <div className="text-[11px] text-navy-800/60 mt-1 ml-2">awaiting decision</div>
          </div>
          <div className="bg-white border border-cream-200 p-4 rounded-sm">
            <div className="text-[10px] uppercase tracking-widest text-green-700">Accepted</div>
            <div className="font-numeric serif-display text-2xl text-navy-800 font-medium mt-1">{accepted.length}</div>
            <div className="text-[11px] text-navy-800/60 mt-1">last 90 days</div>
          </div>
          <div className="bg-white border border-cream-200 p-4 rounded-sm">
            <div className="text-[10px] uppercase tracking-widest text-red-600">Rejected</div>
            <div className="font-numeric serif-display text-2xl text-navy-800 font-medium mt-1">{rejected.length}</div>
            <div className="text-[11px] text-navy-800/60 mt-1">last 90 days</div>
          </div>
          <div className="bg-white border border-cream-200 p-4 rounded-sm">
            <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Acceptance rate</div>
            <div className="font-numeric serif-display text-2xl text-navy-800 font-medium mt-1">{acceptanceRate}%</div>
            <div className="text-[11px] text-navy-800/60 mt-1">cumulative</div>
          </div>
        </div>

        {/* Open */}
        <section className="bg-white border border-cream-200 rounded-sm">
          <div className="px-5 py-4 border-b border-cream-100 flex items-center justify-between">
            <div>
              <h3 className="serif-display text-base text-navy-800 font-medium accent-underline inline-block">Open recommendations</h3>
              <p className="text-xs text-navy-800/60 mt-3">Click to view full reasoning &amp; take action</p>
            </div>
            <span className="text-[10px] bg-saffron-100 text-saffron-700 px-2 py-0.5 rounded font-medium font-numeric">
              {open.length} PENDING
            </span>
          </div>
          <div className="divide-y divide-cream-100">
            {all.isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="px-5 py-4"><div className="h-3 w-3/4 bg-cream-200 rounded animate-pulse" /></div>
              ))
            ) : open.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-navy-800/50">No open recommendations</div>
            ) : (
              open.map((r, i) => <RecCard key={r.id} rec={r} idx={i} onClick={() => setSelected(r.id)} />)
            )}
          </div>
        </section>

        {/* Accepted + Rejected side by side */}
        <section className="grid grid-cols-2 gap-5">
          <div className="bg-white border border-cream-200 rounded-sm">
            <div className="px-5 py-4 border-b border-cream-100">
              <h3 className="serif-display text-base text-navy-800 font-medium accent-underline inline-block">Accepted &amp; acted</h3>
              <p className="text-xs text-navy-800/60 mt-3">Track outcomes of acted-upon recommendations</p>
            </div>
            <div className="divide-y divide-cream-100">
              {accepted.length === 0 ? (
                <div className="px-5 py-6 text-center text-xs text-navy-800/50">None yet</div>
              ) : (
                accepted.map((r) => <RecCard key={r.id} rec={r} onClick={() => setSelected(r.id)} />)
              )}
            </div>
          </div>

          <div className="bg-white border border-cream-200 rounded-sm">
            <div className="px-5 py-4 border-b border-cream-100">
              <h3 className="serif-display text-base text-navy-800 font-medium accent-underline inline-block">Rejected with reason</h3>
              <p className="text-xs text-navy-800/60 mt-3">Feedback loop — improves future recommendations</p>
            </div>
            <div className="divide-y divide-cream-100">
              {rejected.length === 0 ? (
                <div className="px-5 py-6 text-center text-xs text-navy-800/50">None yet</div>
              ) : (
                rejected.map((r) => <RecCard key={r.id} rec={r} onClick={() => setSelected(r.id)} />)
              )}
            </div>
          </div>
        </section>
      </div>

      <RecommendationDrawer open={!!selected} code={selected} onClose={() => setSelected(null)} />
    </>
  );
}
